import usePharmacyFilterViewModel, {
  NearbyPharmacyFilterCriteria,
} from '@/features/pharmacies/shared/hooks/usePharmacyFilterViewModel';
import { Tab } from '@/hooks/pharmacy/useFindPharmacyCategoryTabs';
import useGetNearbyPharmacySortsAndFiltersWithSubFilters from '@/hooks/pharmacy/useGetNearbyPharmacySortsAndFiltersWithSubFilters';
import usePharmacyLocation from '@/hooks/pharmacy/usePharmacyLocation';
import useTreatmentMedicinesByTreatmentId from '@/hooks/pharmacy/useTreatmentMedicinesByTreatmentId';
import { MedicineFilterModel, NearbyPharmacyFilterModel } from '@/models/pharmacy';
import { Client } from '@/types/app';
import {
  DispensationUnitVo,
  ENearbyPharmacySearchSortType,
  NearbyPharmacyMedicineVo,
  NearbyPharmacySortsVo,
} from '@/types/pharmacy';
import { useCallback, useEffect, useState } from 'react';

export interface NonBenefitPharmacyFilterViewModelParams {
  client: Client;
  selectedTab: Tab | null;
  defaultSort: ENearbyPharmacySearchSortType;
  onApplyFilter: (filter: NearbyPharmacyFilterCriteria) => void;
  onClickCurrentLocation?: () => void;
}

export interface MedicineInfo extends NearbyPharmacyMedicineVo {
  isSelected: boolean;
}

export type UseNonBenefitPharmacyFilterViewModel = {
  // 공통 상태 및 메서드 (usePharmacyFilterViewModel에서 상속)
  localFilterCriteria: NearbyPharmacyFilterCriteria;
  setLocalFilterCriteria: (criteria: NearbyPharmacyFilterCriteria) => void;
  toggleFilter: (filter: NearbyPharmacyFilterModel) => void;
  searchFiltersHook: ReturnType<typeof usePharmacyFilterViewModel>['searchFiltersHook'];

  // 위치 관련
  locationHook: ReturnType<typeof usePharmacyLocation>;
  addressActions: {
    openAddressSelectModal: () => {
      addresses: ReturnType<typeof usePharmacyLocation>['addresses'];
      onOpenAddressListScreen: ReturnType<typeof usePharmacyLocation>['goToAppMyPageAddressScreen'];
      onRequestCurrentPosition: (() => void) | undefined;
    };
  };

  // 정렬 관련 상태 및 메서드
  isSortBottomSheetOpen: boolean;
  openSortBottomSheet: () => void;
  closeSortBottomSheet: () => void;

  // 필터 및 정렬 핸들러
  handleFilterClick: (filter: NearbyPharmacyFilterModel) => void;
  handleSortSelected: (sort: NearbyPharmacySortsVo) => void;

  // 약 관련 상태 및 메서드
  isMedicineInfoOpen: boolean;
  handleMedicineInfoClick: () => void;
  handleCloseMedicineInfo: () => void;
  treatmentMedicines: NearbyPharmacyMedicineVo[];
  handleMedicineClick: (medicine: NearbyPharmacyMedicineVo) => void;
  handleDispensationUnitClick: (unit: DispensationUnitVo) => void;
  shouldShowTreatmentMedicineFilter: () => boolean;
  isMedicineSelected: (medicine: NearbyPharmacyMedicineVo) => boolean;
  getSelectedMedicineUnits: () => DispensationUnitVo[];
  isDispensationUnitSelected: (unit: DispensationUnitVo) => boolean;
  shouldShowDiagnosisPriceFilter: () => boolean;
};

export function useNonBenefitPharmacyFilterViewModel({
  selectedTab,
  defaultSort,
  onApplyFilter,
  onClickCurrentLocation,
}: NonBenefitPharmacyFilterViewModelParams): UseNonBenefitPharmacyFilterViewModel {
  // 정렬 및 필터 옵션 가져오기
  const { nearbyPharmacySortsAndFilters } = useGetNearbyPharmacySortsAndFiltersWithSubFilters({
    diagnosisId: selectedTab?.id || '',
    enabled: !!selectedTab?.id,
  });

  // 처방약 목록 가져오기
  const { data: treatmentMedicinesData } = useTreatmentMedicinesByTreatmentId(selectedTab?.diagnosisTypeId || '');

  // 공통 필터 뷰모델 초기화
  const commonFilterViewModel = usePharmacyFilterViewModel({
    selectedTab,
    defaultSort,
    nearbyPharmacySortsAndFilters,
    onApplyFilter,
  });

  // 약국 위치 관련 훅 초기화
  const locationHook = usePharmacyLocation();

  // 약 정보 관련 상태
  const [isMedicineInfoOpen, setIsMedicineInfoOpen] = useState(false);
  const [isSortBottomSheetOpen, setIsSortBottomSheetOpen] = useState(false);

  // 주소 관련 액션
  const addressActions = {
    openAddressSelectModal: () => ({
      addresses: locationHook.addresses,
      onOpenAddressListScreen: locationHook.goToAppMyPageAddressScreen,
      onRequestCurrentPosition: onClickCurrentLocation,
    }),
  };

  // 바텀시트 핸들러
  const openSortBottomSheet = () => setIsSortBottomSheetOpen(true);
  const closeSortBottomSheet = () => setIsSortBottomSheetOpen(false);
  const handleMedicineInfoClick = () => setIsMedicineInfoOpen(true);
  const handleCloseMedicineInfo = () => setIsMedicineInfoOpen(false);

  // 필터 선택 핸들러
  const handleFilterClick = (filter: NearbyPharmacyFilterModel) => {
    commonFilterViewModel.toggleFilter(filter);
  };

  // 정렬 선택 핸들러
  const handleSortSelected = useCallback(
    (sort: NearbyPharmacySortsVo) => {
      const newCriteria: NearbyPharmacyFilterCriteria = {
        selectedMedicine: commonFilterViewModel.localFilterCriteria.selectedMedicine,
        selectedCommonFilters: commonFilterViewModel.localFilterCriteria.selectedCommonFilters,
        includeDiagnosisPriceFilter: commonFilterViewModel.localFilterCriteria.includeDiagnosisPriceFilter,
        selectedSortMethod: sort,
      };
      onApplyFilter(newCriteria);
      closeSortBottomSheet();
    },
    [commonFilterViewModel.localFilterCriteria, onApplyFilter]
  );

  // 약 관련 핸들러
  const handleMedicineClick = useCallback(
    (medicine: NearbyPharmacyMedicineVo) => {
      if (!medicine.dispensationUnits.length) {
        return;
      }

      // 기본 단위 찾기 (isDefault가 true인 단위 또는 첫 번째 단위)
      const defaultUnit = medicine.dispensationUnits.find((unit) => unit.isDefault) || medicine.dispensationUnits[0];
      const newMedicineFilter = new MedicineFilterModel(medicine, defaultUnit, medicine.treatmentMedicineId);

      const newCriteria = {
        ...commonFilterViewModel.localFilterCriteria,
        selectedMedicine: newMedicineFilter,
      };
      commonFilterViewModel.setLocalFilterCriteria(newCriteria);
      onApplyFilter(newCriteria);
    },
    [commonFilterViewModel, onApplyFilter]
  );

  const handleDispensationUnitClick = useCallback(
    (unit: DispensationUnitVo) => {
      if (!commonFilterViewModel.localFilterCriteria.selectedMedicine?.treatmentMedicine) {
        return;
      }

      const newMedicineFilter = new MedicineFilterModel(
        commonFilterViewModel.localFilterCriteria.selectedMedicine.treatmentMedicine,
        unit,
        commonFilterViewModel.localFilterCriteria.selectedMedicine.treatmentMedicineId
      );
      const newCriteria = {
        ...commonFilterViewModel.localFilterCriteria,
        selectedMedicine: newMedicineFilter,
        includeDiagnosisPriceFilter: commonFilterViewModel.localFilterCriteria.includeDiagnosisPriceFilter,
      };
      commonFilterViewModel.setLocalFilterCriteria(newCriteria);
      onApplyFilter(newCriteria);
    },
    [commonFilterViewModel, onApplyFilter]
  );

  // 조건부 UI 표시 관련 함수
  const shouldShowTreatmentMedicineFilter = useCallback(() => {
    return !!treatmentMedicinesData?.length;
  }, [treatmentMedicinesData]);

  const shouldShowDiagnosisPriceFilter = useCallback(() => {
    return !!commonFilterViewModel.localFilterCriteria.selectedMedicine;
  }, [commonFilterViewModel.localFilterCriteria.selectedMedicine]);

  // 약/단위 선택 관련 헬퍼 함수
  const isMedicineSelected = useCallback(
    (medicine: NearbyPharmacyMedicineVo) => {
      return (
        commonFilterViewModel.localFilterCriteria.selectedMedicine?.treatmentMedicine.treatmentMedicineId ===
        medicine.treatmentMedicineId
      );
    },
    [commonFilterViewModel.localFilterCriteria.selectedMedicine]
  );

  const getSelectedMedicineUnits = useCallback(() => {
    return commonFilterViewModel.localFilterCriteria.selectedMedicine?.treatmentMedicine.dispensationUnits || [];
  }, [commonFilterViewModel.localFilterCriteria.selectedMedicine]);

  const isDispensationUnitSelected = useCallback(
    (unit: DispensationUnitVo) => {
      return (
        commonFilterViewModel.localFilterCriteria.selectedMedicine?.dispensationUnit?.dispensationUnit ===
        unit.dispensationUnit
      );
    },
    [commonFilterViewModel.localFilterCriteria.selectedMedicine]
  );

  // 초기 약 선택 설정
  useEffect(() => {
    // 약이 있고 아직 선택되지 않았을 때만 처리
    if (!treatmentMedicinesData?.length || commonFilterViewModel.localFilterCriteria.selectedMedicine) {
      return;
    }

    const firstMedicine = treatmentMedicinesData[0];
    const defaultUnit =
      firstMedicine.dispensationUnits.find((unit) => unit.isDefault) || firstMedicine.dispensationUnits[0];
    const newMedicineFilter = new MedicineFilterModel(firstMedicine, defaultUnit, firstMedicine.treatmentMedicineId);

    const newCriteria = {
      ...commonFilterViewModel.localFilterCriteria,
      selectedMedicine: newMedicineFilter,
    };
    commonFilterViewModel.setLocalFilterCriteria(newCriteria);
    onApplyFilter(newCriteria);
  }, [treatmentMedicinesData, commonFilterViewModel, onApplyFilter]);

  return {
    // 공통 상태 및 메서드
    ...commonFilterViewModel,
    locationHook,
    addressActions,

    // 정렬 관련 상태 및 메서드
    isSortBottomSheetOpen,
    openSortBottomSheet,
    closeSortBottomSheet,

    // 필터 및 정렬 핸들러
    handleFilterClick,
    handleSortSelected,

    // 약 관련 상태 및 메서드
    isMedicineInfoOpen,
    handleMedicineInfoClick,
    handleCloseMedicineInfo,
    treatmentMedicines: treatmentMedicinesData || [],
    handleMedicineClick,
    handleDispensationUnitClick,
    shouldShowTreatmentMedicineFilter,
    isMedicineSelected,
    getSelectedMedicineUnits,
    isDispensationUnitSelected,
    shouldShowDiagnosisPriceFilter,
  };
}
